<template>
  <b-modal
    :visible="visible"
    centered
    hide-header
    hide-footer
    no-close-on-backdrop
    modal-class="cfm"
    content-class="cfm__content"
    body-class="p-0"
    size="sm"
    @hidden="$emit('close')"
    @show="reset"
  >
    <div class="cfm__box">
      <header class="cfm__head">
        <button
          type="button"
          class="cfm__icon"
          :aria-label="$t('chat.cancel')"
          @click="$emit('close')"
        >
          <feather-icon
            icon="XIcon"
            size="20"
          />
        </button>
        <strong>{{ $t('chat.forwardHeader') }}</strong>
      </header>

      <label class="cfm__search">
        <feather-icon
          icon="SearchIcon"
          size="16"
        />
        <input
          v-model="query"
          type="search"
          :placeholder="$t('chat.forwardSearch')"
          :aria-label="$t('chat.forwardSearch')"
        >
      </label>

      <div class="cfm__list">
        <template v-for="group in groups">
          <h6
            :key="`${group.key}-title`"
            class="cfm__section"
          >{{ group.title }}</h6>
          <label
            v-for="target in group.items"
            :key="target.key"
            class="cfm__row"
            :class="{ 'cfm__row--off': isBlocked(target) }"
          >
            <input
              type="checkbox"
              class="cfm__check"
              :checked="isChosen(target)"
              :disabled="isBlocked(target)"
              @change="toggle(target)"
            >
            <chat-avatar
              :name="target.name"
              :photo="target.photo"
              :size="42"
            />
            <span class="cfm__name">{{ target.name }}</span>
          </label>
        </template>
        <p
          v-if="!groups.length"
          class="cfm__empty"
        >{{ $t('chat.forwardNoResults') }}</p>
      </div>

      <transition name="cfm-bar">
        <footer
          v-if="chosen.length"
          class="cfm__bar"
        >
          <span class="cfm__picked">{{ chosen.map(t => t.name).join(', ') }}</span>
          <button
            type="button"
            class="cfm__send"
            :disabled="busy"
            :title="$t('chat.forwardSend')"
            :aria-label="$t('chat.forwardSend')"
            @click="$emit('confirm', chosen)"
          >
            <b-spinner
              v-if="busy"
              small
            />
            <feather-icon
              v-else
              icon="SendIcon"
              size="20"
            />
          </button>
        </footer>
      </transition>
    </div>
  </b-modal>
</template>

<script>
import ChatAvatar from './ChatAvatar.vue'

export default {
  name: 'ChatForwardModal',

  components: { ChatAvatar },

  props: {
    visible: { type: Boolean, default: false },
    // Conversas existentes e contatos online ainda sem conversa, já no formato { key, name, photo, conversationId | userId }.
    recent: { type: Array, default: () => [] },
    contacts: { type: Array, default: () => [] },
    max: { type: Number, default: 5 },
    busy: { type: Boolean, default: false },
  },

  data() {
    return { query: '', chosen: [] }
  },

  computed: {
    groups() {
      const needle = this.query.trim().toLowerCase()
      const filter = list => list.filter(t => !needle || t.name.toLowerCase().includes(needle))

      return [
        { key: 'recent', title: this.$t('chat.forwardRecent'), items: filter(this.recent) },
        { key: 'contacts', title: this.$t('chat.forwardOnline'), items: filter(this.contacts) },
      ].filter(group => group.items.length)
    },
  },

  methods: {
    reset() {
      this.query = ''
      this.chosen = []
    },

    isChosen(target) {
      return this.chosen.some(t => t.key === target.key)
    },

    // No limite de destinos só dá para desmarcar.
    isBlocked(target) {
      return !this.isChosen(target) && this.chosen.length >= this.max
    },

    toggle(target) {
      this.chosen = this.isChosen(target) ? this.chosen.filter(t => t.key !== target.key) : this.chosen.concat(target)
    },
  },
}
</script>

<!-- O modal é renderizado fora do chat (no body): o estilo precisa ser global e não pode usar as variáveis do chat. -->
<style>
.cfm__content { overflow: hidden; border-radius: 14px; }
.cfm__box { display: flex; flex-direction: column; height: min(78vh, 600px); color: #3b3a4a; background: #fff; }
.cfm__head { display: flex; align-items: center; gap: 14px; padding: 14px 16px 6px; }
.cfm__head strong { font-size: 1.05rem; }

.cfm__icon {
  display: inline-flex;
  padding: 4px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: inherit;
}

.cfm__icon:hover { background: rgba(127, 127, 127, 0.16); }

.cfm__search {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 8px 16px 6px;
  padding: 8px 14px;
  border: 1px solid #ebe9f1;
  border-radius: 22px;
  background: #f4f3f8;
  color: #7d7b8c;
}

.cfm__search:focus-within { border-color: #7367f0; }

.cfm__search input {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: inherit;
  font-size: 0.9rem;
}

.cfm__list { flex: 1; padding-bottom: 8px; overflow-y: auto; }

.cfm__section {
  margin: 12px 0 4px;
  padding: 0 20px;
  color: #7d7b8c;
  font-size: 0.78rem;
  font-weight: 500;
}

.cfm__row {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 0;
  padding: 8px 20px;
  cursor: pointer;
}

.cfm__row:hover { background: rgba(127, 127, 127, 0.1); }
.cfm__row--off { cursor: not-allowed; opacity: 0.45; }

.cfm__check { flex: none; width: 18px; height: 18px; accent-color: #7367f0; }

.cfm__name {
  min-width: 0;
  overflow: hidden;
  font-size: 0.95rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cfm__empty { padding: 24px 20px; color: #7d7b8c; text-align: center; }

.cfm__bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-top: 1px solid #ebe9f1;
  background: #f4f3f8;
}

.cfm__picked {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-size: 0.88rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cfm__send {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 50%;
  background: #7367f0;
  color: #fff;
  box-shadow: 0 2px 8px rgba(115, 103, 240, 0.4);
}

.cfm__send:hover { background: #5e50ee; }
.cfm__send:disabled { opacity: 0.7; }

.cfm-bar-enter-active, .cfm-bar-leave-active { transition: transform 0.18s ease, opacity 0.18s ease; }
.cfm-bar-enter, .cfm-bar-leave-to { opacity: 0; transform: translateY(12px); }

.dark-layout .cfm__box { background: #283046; color: #d0d2d6; }
.dark-layout .cfm__search, .dark-layout .cfm__bar { border-color: #3b4253; background: #343d55; }
.dark-layout .cfm__section, .dark-layout .cfm__empty { color: #9a9cae; }
</style>
